import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import { FormattedMessage, useIntl, defineMessages } from 'react-intl';

import { Colours, Sizes, navigateWindowTo, PrimaryButton, StringInput } from '@ratehub/base-ui';

import closeSearchPopup from '../functions/closeSearchPopup';


/**
 * SearchForm Component
 * Renders a search input that redirects to /search?s=query
 * @param {object} props - Component props
 * @param {Function} [props.onSearch] - Optional callback function called with the query string when search is submitted
 * @param {string} [props.initialQuery=''] - Initial value for the search query input
 * @returns {React.ReactElement} The rendered search form component
 */
function SearchForm({ onSearch, initialQuery = '' }) {
    const intl = useIntl();
    const [ query, setQuery ] = useState(initialQuery);
    const [ showError, setShowError ] = useState(false);

    // Determine if we should display the error
    const shouldShowError = showError && !query.trim();

    useEffect(() => {
        setQuery(initialQuery);
    }, [ initialQuery ]);

    const handleChange = (newValue) => {
        setQuery(newValue);
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        if (!query.trim()) {
            setShowError(true);
            return;
        }

        setShowError(false);

        // Close the search popup before navigating
        closeSearchPopup();

        if (onSearch) {
            onSearch(query);
        } else {
            navigateWindowTo('/search', {
                queryParams: { s: query },
            });
        }
    };

    const placeholderText = intl.formatMessage(MESSAGES.placeholder);

    return (
        <Container
            onSubmit={handleSubmit}
            className={`rh-width-100p rh-display-flex rh-align-items-center rh-mx-auto ${shouldShowError
                ? 'has-error'
                : ''}`}
        >
            <div className="input-wrapper rh-flex-1 rh-position-relative">
                <StringInput
                    name="search-query"
                    value={query}
                    onChange={handleChange}
                    placeholder={placeholderText}
                    aria-label={placeholderText}
                    className="search-input"
                    isRequired={false}
                />
                <If condition={shouldShowError}>
                    <span className="error-message">
                        <FormattedMessage {...MESSAGES.errorMessage} />
                    </span>
                </If>
                <button
                    type="submit"
                    aria-label={intl.formatMessage(MESSAGES.searchButtonAriaLabel)}
                    data-name="mobile-search-form-button"
                    className="mobile-search-button rh-position-absolute rh-top-0 rh-bottom-0 rh-display-flex rh-align-items-center rh-justify-content-center rh-border-width-0 rh-bg-transparent rh-p-0 rh-cursor-pointer"
                >
                    <svg
                        width="18"
                        height="18"
                        viewBox="0 0 18 18"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                    >
                        <path
                            d="M6.68294 12.3659C9.82155 12.3659 12.3659 9.82154 12.3659 6.68294C12.3659 3.54434 9.82155 1 6.68294 1C3.54434 1 1 3.54434 1 6.68294C1 9.82154 3.54434 12.3659 6.68294 12.3659Z"
                            stroke="#010101"
                            strokeWidth="2"
                            strokeMiterlimit="10"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                        <path
                            d="M10.7062 10.7007L16.7043 16.6988"
                            stroke="#010101"
                            strokeWidth="2"
                            strokeMiterlimit="10"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                    </svg>
                </button>
            </div>
            <PrimaryButton
                type="submit"
                variant="blueberry-dark"
                size="medium"
                data-name="desktop-search-form-button"
                className="desktop-search-button rh-flex-shrink-0"
            >
                <FormattedMessage {...MESSAGES.searchButton} />
            </PrimaryButton>
        </Container>
    );
}

SearchForm.propTypes = {
    onSearch: PropTypes.func,
    initialQuery: PropTypes.string,
};

const MESSAGES = defineMessages({
    placeholder: {
        id: 'searchForm.placeholder',
        defaultMessage: 'Type in any keyword',
    },
    errorMessage: {
        id: 'searchForm.errorMessage',
        defaultMessage: 'Please enter a valid value',
    },
    searchButton: {
        id: 'searchForm.searchButton',
        defaultMessage: 'Search',
    },
    searchButtonAriaLabel: {
        id: 'searchForm.searchButtonAriaLabel',
        defaultMessage: 'Search',
    },
});

const Container = styled.form`
    gap: ${Sizes.SPACING.HALF};

    @media (min-width: 1023px) {
        max-width: 60.64rem;
    }

    > .input-wrapper {
        flex: 1;
        min-width: 0;

        > .search-input {
            input {
                height: ${Sizes.SPACING.THREE}; /* 40px ≈ 2.857rem → 3rem (42px) */
                padding: 0 ${Sizes.SPACING.ONE};
                padding-right: ${Sizes.SPACING.THREE};
                font-size: ${Sizes.FONTS.S}; /* 1rem - 16px on desktop */
                line-height: ${Sizes.LINE_HEIGHT.M}; /* 1.571 → 1.5 */

                @media (min-width: 1023px) {
                    padding-right: ${Sizes.SPACING.ONE};
                }
            }
        }

        > .error-message {
            position: absolute;
            top: 100%;
            left: 50%;
            transform: translateX(-50%);
            background-color: ${Colours.STRAWBERRY};
            color: ${Colours.COCONUT};
            font-size: ${Sizes.FONTS['2XS']};
            padding: ${Sizes.SPACING.QUARTER} ${Sizes.SPACING.SEVEN_EIGHTHS}; /* 4px ≈ 0.286rem → 0.25rem, 12px ≈ 0.857rem → 0.875rem */
            margin-top: ${Sizes.SPACING.QUARTER}; /* 4px ≈ 0.286rem → 0.25rem */
            border-radius: ${Sizes.SPACING.EIGHTH}; /* 2px ≈ 0.143rem → 0.125rem */
            white-space: nowrap;

            &::before {
                content: '';
                position: absolute;
                top: -4px;
                left: 50%;
                transform: translateX(-50%);
                width: 0;
                height: 0;
                border-left: 5px solid transparent;
                border-right: 5px solid transparent;
                border-bottom: 5px solid ${Colours.STRAWBERRY};
            }
        }

        > .mobile-search-button {
            right: 0;
            width: ${Sizes.SPACING.THREE};
            transition: all 0.2s ease;

            svg {
                display: block;
                transition: transform 0.2s ease;
            }

            &:hover svg {
                transform: scale(1.1);
            }

            &:active svg {
                transform: scale(0.95);
            }

            &:focus {
                outline: 2px solid ${Colours.BLUEBERRY_DARK};
                outline-offset: -2px;
            }

            @media (min-width: 1023px) {
                display: none;
            }
        }
    }

    > .desktop-search-button {
        height: ${Sizes.SPACING.THREE} !important; /* 40px ≈ 2.857rem → 3rem (42px) */
        min-height: ${Sizes.SPACING.THREE};
        max-height: ${Sizes.SPACING.THREE};
        padding: 0 ${Sizes.SPACING.TWO};
        font-weight: 500;
        font-size: ${Sizes.FONTS.S};
        line-height: ${Sizes.LINE_HEIGHT.M}; /* 1.429 → 1.5 */
        width: ${Sizes.SPACING.EIGHT}; /* 120px ≈ 8.57rem → 8rem (112px) */

        @media (max-width: 1022px) {
            display: none;
        }
    }

    /* Error state styles */
    &.has-error {
        > .input-wrapper > .search-input input {
            border-color: ${Colours.STRAWBERRY};

            &::placeholder {
                color: ${Colours.STRAWBERRY};
            }

            &:focus {
                border-color: ${Colours.STRAWBERRY};
                box-shadow: 0 0 0 1px ${Colours.STRAWBERRY};
            }
        }
    }
`;

export default SearchForm;
